import type { MonitorGroup, MonitorStatus } from 'shared';

export const groupLabels: Record<MonitorGroup, string> = {
  foundation: '基础设施',
  realtime: '实时连接',
  async: '异步执行',
  external: '外部服务',
};
export const statusLabels: Record<MonitorStatus, string> = {
  healthy: '正常',
  degraded: '部分异常',
  unavailable: '不可用',
  unknown: '未验证',
  disabled: '未启用',
};
export const statusColors: Record<MonitorStatus, string> = {
  healthy: 'success',
  degraded: 'warning',
  unavailable: 'error',
  unknown: 'default',
  disabled: 'default',
};
export const taskStatusLabels: Record<string, string> = {
  all: '全部状态',
  queued: '排队中',
  running: '执行中',
  waiting: '等待中',
  cancelling: '取消中',
  failed: '失败',
  interrupted: '中断',
  completed: '已完成',
  cancelled: '已取消',
  submitting: '提交中',
  processing: '生成中',
  downloading: '下载中',
  succeeded: '已完成',
  active: '执行中',
  delayed: '延迟执行',
  prioritized: '优先排队',
  'waiting-children': '等待子任务',
  unknown: '未知',
};
export const kindLabels: Record<string, string> = {
  all: '全部类型',
  primary: '主会话',
  delegate: '委派',
  dream: '记忆整理',
  agent: 'Agent 通信',
  image: '图片',
  video: '视频',
  audio: '语音',
  unknown: '未知',
};
export const metricLabels: Record<string, string> = {
  connections: '连接',
  queued: '排队中',
  cancelling: '取消中',
  submitting: '提交中',
  processing: '生成中',
  downloading: '下载中',
  failuresLast24Hours: '近 24 小时异常',
  users: '在线用户',
  devices: '设备',
  web: '网页',
  desktop: '桌面',
  mobile: '移动端',
  waiting: '排队',
  active: '执行中',
  delayed: '延迟',
  retainedFailed: '保留失败 Job',
  retainedCompleted: '保留完成 Job',
  paused: '已暂停',
  consumers: '有效消费者',
  lastHeartbeatAt: '最近消费者心跳',
  reportingProcesses: '上报进程',
  sessions: '运行进程',
  initialized: '已初始化',
  pendingRequests: '等待响应',
  configuredAccounts: '已配置账户',
  activeAccounts: '已绑定账户',
  errorAccounts: '异常账户',
  expected: '当前连接',
  connected: '已认证连接',
  model: '模型',
  provider: '供应商',
  mediaKind: '媒体类型',
  scope: '范围',
  isDefault: '默认模型',
  check: '检查范围',
  process: '服务进程',
  uptimeSeconds: '运行时长（秒）',
  ping: 'Ping',
  query: '读取验证',
  enabled: '已启用',
  readable: '可读',
  writable: '可写',
  image: '沙箱镜像',
  imageAvailable: '镜像可用',
  containers: '现有容器',
  running: '运行中',
  stopped: '停止容器',
  agentId: 'Agent ID',
  rootRunId: '根 Run ID',
  heartbeatAt: '租约心跳',
  leaseExpiresAt: '租约到期',
  recoveryCount: '恢复次数',
  cancellationRequestedAt: '取消请求时间',
  progress: '进度（%）',
  modelName: '模型',
  credentialSource: '凭据来源',
  providerTaskId: '供应商任务 ID',
  expiresAt: '超时时间',
  attemptsMade: '已执行次数',
  attempts: '最多尝试次数',
  processedAt: '开始时间',
  finishedAt: '结束时间',
};
export const errorLabels: Record<string, string> = {
  UNKNOWN: '未知（历史错误未分类）',
  CHECK_FAILED: '只读检查失败',
  NOT_CONFIGURED: '配置不完整',
  QUEUE_NOT_BOUND: '队列未绑定',
  QUEUE_PAUSED: '队列已暂停',
  CONSUMER_MISSING: '缺少有效消费者心跳',
  GATEWAY_NOT_BOUND: '实时网关未绑定',
  DOCKER_OR_IMAGE_UNAVAILABLE: 'Docker 或镜像不可用',
  NETWORK: '网络连接失败',
  TIMEOUT: '请求超时',
  AUTHENTICATION: '认证失败',
  RATE_LIMITED: '供应商限流',
  UPSTREAM_UNAVAILABLE: '上游服务不可用',
  BUSINESS_REJECTED: '业务请求被拒绝',
  CANCELLED: '请求已取消',
  PROVIDER_ERROR: '供应商异常',
  SUBMISSION_UNCERTAIN: '提交结果不确定',
  EMPTY_OUTPUT: '产物为空',
  PROVIDER_REJECTED: '供应商拒绝请求',
};

export function formatTime(value?: string | null): string {
  if (!value || !Number.isFinite(Date.parse(value))) return '—';
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date(value));
}
export function metricValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (key.endsWith('At')) return formatTime(String(value));
  const text = String(value);
  return (
    (
      {
        platform: '平台托管',
        byok: '用户自带',
        api: 'API',
        mock: '模拟供应商',
        qiniu: '七牛',
        local: '本地',
        'aliyun-bailian': '阿里云百炼',
        volcengine: '火山引擎',
      } as Record<string, string>
    )[text] ??
    kindLabels[text] ??
    text
  );
}
